import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { visitLink } from '../visitLinks';
import { patientIdForChart } from '@/features/patients/directory/patientsMock';
import type { EstimateLine, FamilyMember, Visit, VisitOpportunity } from '../readinessMock';
import {
  MOCK_NOW_MIN,
  SETTINGS_HIDDEN_OPPORTUNITIES,
  checkIsStale,
  cappedTotals,
  checkWhen,
  clockTime,
  usdCents,
  usdWhole,
  visibleOpportunities,
} from '../readinessMock';

/**
 * The visit modal (rcm-22 §4, D170; styled to the FrontDesk appointment design,
 * minus the front-desk-only controls — no no-show status, operatory, kiosk code
 * or resend). Click a visit → a centred dialog over a scrim. It answers "is this
 * visit's insurance ready, and what does the patient pay?" It never books, moves
 * or shows production as a target. Esc, ✕ and the scrim close it.
 */
export function VisitModal({
  visit,
  providerName,
  dayLabel,
  onClose,
}: {
  visit: Visit;
  providerName: string;
  /** "Today" or "Mon, Oct 19". */
  dayLabel: string;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [rechecking, setRechecking] = useState(false);
  const [rechecked, setRechecked] = useState(false);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Mock "Check eligibility": shows "Checking now…" briefly, then a fresh check.
  useEffect(() => {
    if (!rechecking) return;
    const t = window.setTimeout(() => {
      setRechecking(false);
      setRechecked(true);
    }, 1400);
    return () => {
      window.clearTimeout(t);
    };
  }, [rechecking]);

  const first = firstName(visit.patient);
  const [nameFirst, ...nameRest] = visit.patient.split(' ');
  const patientId = patientIdForChart(visit.chart);
  const start = clockTime(visit.startMin);
  const end = clockTime(visit.startMin + visit.lengthMin);
  const check = checkSummary(visit, rechecking, rechecked);
  const production = usdWhole(visit.estimate.reduce((s, l) => s + l.fee, 0));
  const titleId = `rcal-m-title-${visit.id}`;
  const linkTo = visitLink(visit);

  return createPortal(
    <div
      className="rcal-m-scrim"
      role="presentation"
      onClick={() => {
        onClose();
      }}
    >
      <div
        className="rcal-m"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <header className="rcal-m-head">
          <div className="rcal-m-id">
            <div className="rcal-lab">
              {dayLabel} · {start} · {providerName}
            </div>
            <h2 id={titleId}>
              {nameFirst}
              {nameRest.length > 0 ? <span className="rcal-m-last"> {nameRest.join(' ')}</span> : null}
            </h2>
            <div className="rcal-m-who">
              {visit.age} · DOB <span className="rcal-mono">{visit.dob}</span> · chart{' '}
              <span className="rcal-mono">{visit.chart}</span> · patient since {visit.since}
              {' · '}
              {patientId ? (
                <Link className="rcal-m-profile" to={`/patients/${patientId}`}>
                  View full profile →
                </Link>
              ) : null}
            </div>
          </div>
          <div className="rcal-m-rt">
            <button
              ref={closeRef}
              type="button"
              className="rcal-m-x"
              aria-label="Close"
              onClick={() => {
                onClose();
              }}
            >
              ✕
            </button>
            {visit.plan !== null ? (
              <div className="rcal-m-elig">
                <button
                  type="button"
                  className="rcal-m-elig-btn"
                  disabled={rechecking}
                  onClick={() => {
                    setRechecked(false);
                    setRechecking(true);
                  }}
                >
                  <span className="rcal-m-ic sm" aria-hidden="true">
                    <Icon name="shield" />
                  </span>
                  Check eligibility
                </button>
                <span className="rcal-m-chk">{check.text}</span>
              </div>
            ) : null}
          </div>
        </header>

        <div className="rcal-m-facts">
          <Fact icon="clock" label="Time" value={`${start} – ${end} · ${String(visit.lengthMin)}m`} />
          <Fact icon="person" label="Provider" value={providerName} />
          <Fact icon="shield" label="Plan" value={visit.plan ?? 'No insurance on file'} />
          <Fact icon="card" label="Member ID" value={visit.member ?? '—'} mono={visit.member !== null} />
          <Fact icon="dollar" label="Production" value={production} />
        </div>

        {visit.notReady ? (
          <div className="rcal-m-warnbar">
            <p>
              <b>{asSentence(visit.notReady.heading)}</b> {visit.notReady.detail}
            </p>
            {linkTo ? <Link to={linkTo.to}>{linkTo.label}</Link> : null}
          </div>
        ) : null}

        <div className="rcal-m-body">
          <OpportunitiesPane visit={visit} />
          <EstimatePane visit={visit} first={first} check={check} />
        </div>

        <FamilyBlock visit={visit} />

        <footer className="rcal-m-foot">
          <p>Opportunities are based on this plan&rsquo;s benefits and treatment history. An estimate, not a guarantee of payment.</p>
          {patientId ? (
            <Link className="rcal-btn ghost" to={`/patients/${patientId}`}>
              Open insurance page
            </Link>
          ) : null}
          <a
            className="rcal-btn primary"
            href={`/patients/${patientId ?? visit.chart}/report?visit=${encodeURIComponent(visit.id)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Print summary
          </a>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/* ---- facts ---- */

function Fact({
  icon,
  label,
  value,
  mono,
}: {
  icon: IconName;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <span className="rcal-m-ic" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <div>
        <div className="rcal-m-fk">{label}</div>
        <div className={`rcal-m-fv${mono ? ' rcal-mono' : ''}`}>{value}</div>
      </div>
    </div>
  );
}

/* ---- icons ---- */

type IconName = 'clock' | 'shield' | 'card' | 'check' | 'person' | 'aligner' | 'tooth' | 'dollar' | 'warn';

const ICON_PATHS: Record<IconName, ReactNode> = {
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.2v5l3.2 2" />
    </>
  ),
  shield: <path d="M12 3.2l7 2.9v4.8c0 4.4-2.9 7.8-7 9.9-4.1-2.1-7-5.5-7-9.9V6.1z" />,
  card: (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2.2" />
      <path d="M7 10.5h3.5M7 14h7.5" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.2 12.4l2.6 2.6 5-5.4" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8.4" r="3.6" />
      <path d="M5.5 19.2c0-3.5 2.9-5.6 6.5-5.6s6.5 2.1 6.5 5.6" />
    </>
  ),
  aligner: (
    <>
      <path d="M4.6 9.2c2.2-2 12.6-2 14.8 0" />
      <path d="M5.4 12.2c1.4 4.4 11.8 4.4 13.2 0" />
      <path d="M9.2 8.4v2.6M14.8 8.4v2.6" />
    </>
  ),
  tooth: (
    <path d="M8.2 3.4C5.9 3.4 4.4 5.2 4.4 7.6c0 2.9 1.5 3.9 2 7.3.4 2.7.6 4.9 2 4.9s1.2-2.9 1.9-2.9.5 2.9 1.9 2.9 1.6-2.2 2-4.9c.5-3.4 2-4.4 2-7.3 0-2.4-1.5-4.2-3.8-4.2-1 0-1.3.5-2.1.5s-1.1-.5-2.1-.5z" />
  ),
  dollar: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.3v9.4" />
      <path d="M14.4 9.5c0-.9-1.1-1.5-2.4-1.5s-2.4.6-2.4 1.7 1.1 1.5 2.4 1.9 2.7.8 2.7 2-1.3 1.7-2.7 1.7-2.4-.5-2.4-1.4" />
    </>
  ),
  warn: (
    <>
      <path d="M12 4.2l8.4 15H3.6z" />
      <path d="M12 10v3.6M12 16.2v.1" />
    </>
  ),
};

function Icon({ name }: { name: IconName }) {
  return <svg viewBox="0 0 24 24">{ICON_PATHS[name]}</svg>;
}

/** Aligner, tooth or dollar, by what the opportunity is about. */
function opportunityIcon(item: VisitOpportunity): IconName {
  if (/align|ortho/i.test(item.name)) return 'aligner';
  if (/crown|tooth|root|filling|sealant|fluoride|#\d/i.test(item.name)) return 'tooth';
  return 'dollar';
}

/** The explicit priority, or a sensible default from the figure. */
function opportunityPriority(item: VisitOpportunity): 'high' | 'medium' | 'low' {
  if (item.priority) return item.priority;
  if (item.amount >= 1000) return 'high';
  if (item.amount >= 250) return 'medium';
  return 'low';
}

/** The right-hand coverage figure: "Up to $1,500" for a plan payment, "$300" for a value. */
function coverageFigure(item: VisitOpportunity): string {
  return item.kind === 'plan' ? `Up to ${usdWhole(item.amount)}` : usdWhole(item.amount);
}

/** The plan's type, from the plan string ("… · PPO", "Cigna DPPO", "MetLife PDP+"). */
function planTypeOf(plan: string | null): string | null {
  if (plan === null) return null;
  if (plan.includes('·')) {
    const parts = plan.split('·');
    return (parts[parts.length - 1] ?? plan).trim();
  }
  const m = /\b(DPPO|PPO|PDP\+?|DHMO|HMO|EPO|POS)\b/i.exec(plan);
  if (m) return m[0].toUpperCase();
  const words = plan.trim().split(' ');
  return words[words.length - 1] ?? plan;
}

/** A visit with a plan that has not ended: the only case that shows opportunities and an estimate. */
function isLive(visit: Visit): boolean {
  return visit.plan !== null && visit.coverageEnded === undefined;
}

function firstName(name: string): string {
  return name.split(' ')[0] ?? name;
}

/** Adds a full stop when a heading doesn't already end in punctuation. */
function asSentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

interface CheckState {
  text: string;
  verified: boolean;
}

/** The header check line ("Checked today, 6:10 AM …") and whether eligibility is verified. */
function checkSummary(visit: Visit, rechecking: boolean, rechecked: boolean): CheckState {
  if (visit.plan === null) return { text: 'Nothing to check until a plan is on file', verified: false };
  if (rechecking) return { text: 'Checking now…', verified: false };
  if (rechecked) {
    const from = visit.sources !== null ? ` · from ${String(visit.sources)} sources` : '';
    return { text: `Checked today, ${clockTime(MOCK_NOW_MIN)}${from}`, verified: true };
  }
  if (visit.checkedAt === null) {
    return { text: visit.state === 'checking' ? 'Checking now' : 'Not checked yet', verified: false };
  }
  const when = checkWhen(visit.checkedAt);
  const from = visit.sources !== null && !visit.checkFailed ? ` · from ${String(visit.sources)} sources` : '';
  return {
    text: `${visit.checkFailed ? 'Last check failed, ' : 'Checked '}${when}${from}`,
    verified: !visit.checkFailed && !checkIsStale(visit.checkedAt),
  };
}

/* ---- opportunities ---- */

/** Why the opportunities pane has nothing to offer, or null when it can show them. */
function opportunityBlock(visit: Visit): string | null {
  if (visit.plan === null) return 'Nothing to show until a plan is on file.';
  if (visit.coverageEnded !== undefined) return `Nothing to show: coverage ended ${visit.coverageEnded}.`;
  if (visit.yearlyMaxLeft === null) return 'Opportunities appear once the benefits are confirmed.';
  return null;
}

function OpportunitiesPane({ visit }: { visit: Visit }) {
  const blocked = opportunityBlock(visit);
  const items = blocked === null ? visibleOpportunities(visit) : [];
  const hidden = SETTINGS_HIDDEN_OPPORTUNITIES;
  const hiddenWords = ['No', 'One', 'Two', 'Three', 'Four'];
  const hiddenCount = hiddenWords[hidden.length] ?? String(hidden.length);

  return (
    <section className="rcal-m-pane" aria-labelledby={`${visit.id}-opps`}>
      <div className="rcal-m-ph">
        <span className="rcal-m-ic sm teal" aria-hidden="true">
          <Icon name="dollar" />
        </span>
        <h3 id={`${visit.id}-opps`}>Opportunities</h3>
        {blocked === null ? (
          <span className="rcal-m-count">
            {items.length} {items.length === 1 ? 'opportunity' : 'opportunities'}
          </span>
        ) : null}
      </div>
      {blocked !== null ? (
        <p className="rcal-m-empty">{blocked}</p>
      ) : items.length === 0 ? (
        <p className="rcal-m-empty">Nothing else to offer for this visit.</p>
      ) : (
        items.map((item) => <OpportunityRow key={item.id} item={item} />)
      )}
      {blocked === null && visit.plan !== null ? (
        <Link className="rcal-m-foot-line" to="/settings/opportunities">
          {`${hiddenCount} more ${hidden.length === 1 ? 'is' : 'are'} hidden by your settings: ${hidden.join(', ')}.`}
        </Link>
      ) : null}
    </section>
  );
}

function OpportunityRow({ item }: { item: VisitOpportunity }) {
  const [open, setOpen] = useState(false);
  const priority = opportunityPriority(item);
  return (
    <div className="rcal-m-op">
      <span className="rcal-m-ic" aria-hidden="true">
        <Icon name={opportunityIcon(item)} />
      </span>
      <span className="rcal-m-ot">
        <span className="rcal-m-oname">
          <b>{item.name}</b>
          <span className={`rcal-m-pri ${priority}`}>{priority[0]?.toUpperCase()}{priority.slice(1)}</span>
        </span>
        <span className="rcal-m-od">
          {item.line}
          <button
            type="button"
            className="rcal-m-nn"
            aria-expanded={open}
            onClick={() => {
              setOpen((o) => !o);
            }}
          >
            Not now
          </button>
        </span>
        {open ? <span className="rcal-m-why">Not now: {item.notNow}</span> : null}
      </span>
      <span className="rcal-m-oa">
        <span className="rcal-m-ol">Est. insurance coverage</span>
        <span className="rcal-m-ov">{coverageFigure(item)}</span>
      </span>
    </div>
  );
}

/* ---- estimate ---- */

function EstimatePane({ visit, first, check }: { visit: Visit; first: string; check: CheckState }) {
  const payer = visit.payer ?? 'Plan';
  let body: ReactNode;
  if (visit.plan === null) {
    body = <p className="rcal-m-empty">No estimate: there is no plan on file to estimate against.</p>;
  } else if (visit.coverageEnded !== undefined) {
    body = <p className="rcal-m-empty">Coverage ended {visit.coverageEnded}. There is no estimate for this visit.</p>;
  } else {
    body = <EstimateBody visit={visit} first={first} payer={payer} />;
  }
  return (
    <section className="rcal-m-pane" aria-labelledby={`${visit.id}-est`}>
      <div className="rcal-m-ph">
        <span className="rcal-m-ic sm teal" aria-hidden="true">
          <Icon name="shield" />
        </span>
        <h3 id={`${visit.id}-est`}>Today&rsquo;s insurance estimate</h3>
        <span className="rcal-m-payer">{visit.payer ?? 'No plan'}</span>
        {check.verified ? (
          <span className="rcal-m-verified">
            <Icon name="check" /> Eligibility verified {check.text.replace(/^Checked\s*/, '').replace(/\s·.*$/, '')}
          </span>
        ) : null}
      </div>
      {body}
      <PlanCards visit={visit} />
      {isLive(visit) ? (
        <p className="rcal-m-note">
          Every figure opens its sources: the fee schedule, the plan rule and the check that confirmed it.
        </p>
      ) : null}
    </section>
  );
}

interface Totals {
  fee: number;
  allowed: number;
  insurance: number;
  patient: number;
}

function totalsOf(lines: EstimateLine[]): Totals {
  return lines.reduce<Totals>(
    (t, l) => ({
      fee: t.fee + l.fee,
      allowed: t.allowed + l.allowed,
      insurance: t.insurance + l.insurance,
      patient: t.patient + l.patient,
    }),
    { fee: 0, allowed: 0, insurance: 0, patient: 0 },
  );
}

function EstimateBody({ visit, first, payer }: { visit: Visit; first: string; payer: string }) {
  const pending = visit.estimate.some((l) => l.mode === 'pending');
  // Lines are quoted before the cap; the hero and the equation use what the plan actually pays.
  const totals = totalsOf(visit.estimate);
  const pays = cappedTotals(visit.estimate, visit.cap ?? null);
  const writeOff = totals.fee - totals.allowed;
  return (
    <>
      <div className="rcal-m-big">
        <span className="rcal-m-lft">
          <span className="rcal-m-bl">{first} pays today, estimated</span>
          <span className="rcal-m-bv">{pending ? 'Not yet known' : usdCents(pays.patient)}</span>
        </span>
        {pending ? null : (
          <span className="rcal-m-eqr">
            <span className="rcal-m-cel">
              <span className="rcal-m-cl">Allowed</span>
              <span className="rcal-m-cv">{usdCents(pays.allowed)}</span>
            </span>
            <span className="rcal-m-op2" aria-hidden="true">−</span>
            <span className="rcal-m-cel">
              <span className="rcal-m-cl">{payer} pays</span>
              <span className="rcal-m-cv">{usdCents(pays.plan)}</span>
            </span>
            <span className="rcal-m-op2" aria-hidden="true">=</span>
            <span className="rcal-m-cel">
              <span className="rcal-m-cl">{first} pays</span>
              <span className="rcal-m-cv">{usdCents(pays.patient)}</span>
            </span>
          </span>
        )}
        <p className="rcal-m-subfee">
          {pending
            ? `Your fees are ${usdCents(totals.fee)}.`
            : `Your fees are ${usdCents(totals.fee)}; ${usdCents(writeOff)} of that is the in-network write-off.`}
        </p>
      </div>

      <table className="rcal-m-pt">
        <thead>
          <tr>
            <th>Procedure</th>
            <th>Your fee</th>
            <th>Allowed</th>
            <th>{payer}</th>
            <th>{first}</th>
          </tr>
        </thead>
        <tbody>
          {visit.estimate.map((line, i) => (
            <EstimateRow key={`${line.label}-${String(i)}`} line={line} />
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td>Totals</td>
            <td className="rcal-mono">{usdCents(totals.fee)}</td>
            <td className="rcal-mono">{usdCents(totals.allowed)}</td>
            <td className="rcal-mono">{pending ? 'Not yet known' : usdCents(totals.insurance)}</td>
            <td className="rcal-mono">{pending ? 'Not yet known' : usdCents(totals.patient)}</td>
          </tr>
        </tfoot>
      </table>

      {visit.cap ? (
        <div className="rcal-m-cap">
          <span className="rcal-m-ic warn" aria-hidden="true">
            <Icon name="warn" />
          </span>
          <span>
            <b>Capped by what the plan has left.</b> {payer} would pay {usdCents(visit.cap.wouldPay)}, but only{' '}
            {usdCents(visit.cap.left)} of the yearly maximum is left, so {first} pays {usdCents(pays.patient)}{' '}
            instead of {usdCents(visit.cap.uncappedPatient)}.
          </span>
        </div>
      ) : null}

      {visit.estimateNote ? <p className="rcal-m-note">{visit.estimateNote}</p> : null}
    </>
  );
}

function EstimateRow({ line }: { line: EstimateLine }) {
  const pending = line.mode === 'pending';
  return (
    <tr>
      <td>
        {line.cdt ? <span className="rcal-m-cdt">{line.cdt}</span> : null}
        {line.label}
      </td>
      <td className="rcal-mono">{usdCents(line.fee)}</td>
      <td className="rcal-mono">{usdCents(line.allowed)}</td>
      <td className="rcal-mono">{pending ? <span className="rcal-m-nyk">Not yet known</span> : usdCents(line.insurance)}</td>
      <td className="rcal-mono">{pending ? <span className="rcal-m-nyk">Not yet known</span> : usdCents(line.patient)}</td>
    </tr>
  );
}

/* ---- benefit cards ---- */

/** What a plan card says when there is no figure: no plan, an ended plan, or not yet known. */
function missingWord(visit: Visit): string {
  if (visit.plan === null) return 'No plan';
  if (visit.coverageEnded !== undefined) return `Coverage ended ${visit.coverageEnded}`;
  return 'Not yet known';
}

function PlanCards({ visit }: { visit: Visit }) {
  const missing = missingWord(visit);
  const annual = visit.yearlyMax !== null ? usdWhole(visit.yearlyMax) : missing;
  const annualSub = visit.yearlyMaxLeft !== null ? `${usdWhole(visit.yearlyMaxLeft)} remaining` : '';
  const ortho = visit.ortho;
  const orthoValue = ortho !== null ? usdWhole(ortho.max) : missing;
  const orthoSub = ortho !== null ? (ortho.left === ortho.max ? 'none used' : `${usdWhole(ortho.left)} remaining`) : '';
  const planType = planTypeOf(visit.plan) ?? missing;
  const year = visit.planYear?.year;
  return (
    <div className="rcal-m-pf">
      <div>
        <div className="rcal-m-fk">Annual max</div>
        <div className="rcal-m-fv">{annual}</div>
        <div className="rcal-m-fs">{annualSub}</div>
      </div>
      <div>
        <div className="rcal-m-fk">Deductible</div>
        <div className="rcal-m-fv">{visit.deductible?.label ?? missing}</div>
        <div className="rcal-m-fs">{visit.deductible?.sub ?? ''}</div>
      </div>
      <div>
        <div className="rcal-m-fk">Ortho lifetime max</div>
        <div className="rcal-m-fv">{orthoValue}</div>
        <div className="rcal-m-fs">{orthoSub}</div>
      </div>
      <div>
        <div className="rcal-m-fk">Plan type</div>
        <div className="rcal-m-fv">{planType}</div>
        <div className="rcal-m-fs">{visit.payer ?? ''}</div>
      </div>
      <div>
        <div className="rcal-m-fk">Effective</div>
        <div className="rcal-m-fv">{year !== undefined ? `01/01/${year}` : missing}</div>
        <div className="rcal-m-fs">{year !== undefined ? `to 12/31/${year}` : ''}</div>
      </div>
    </div>
  );
}

/* ---- family ---- */

/** "Others on this plan": one row per family member on the same plan (rcm-22 §4). */
function FamilyBlock({ visit }: { visit: Visit }) {
  if (visit.plan === null) return null;
  return (
    <section className="rcal-m-fam" aria-label="Others on this plan">
      <div className="rcal-m-ph">
        <span className="rcal-m-ic sm teal" aria-hidden="true">
          <Icon name="person" />
        </span>
        <h3>Others on this plan</h3>
        <span className="rcal-m-count">
          {visit.family.length} {visit.family.length === 1 ? 'member' : 'members'}
        </span>
      </div>
      {visit.family.length === 0 ? (
        <p className="rcal-m-empty">No one else on this plan is on file.</p>
      ) : (
        <table className="rcal-m-ft">
          <thead>
            <tr>
              <th>Member</th>
              <th>Recall status</th>
              <th>Unscheduled treatment</th>
              <th>Benefits remaining</th>
            </tr>
          </thead>
          <tbody>
            {visit.family.map((m) => (
              <FamilyRow key={m.chart} m={m} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function FamilyRow({ m }: { m: FamilyMember }) {
  const memberId = patientIdForChart(m.chart);
  const max = m.benefitsMax ?? 1500;
  const pct = Math.max(0, Math.min(100, Math.round((m.yearlyMaxLeft / max) * 100)));
  return (
    <tr>
      <td>
        {memberId ? (
          <Link className="rcal-m-fname" to={`/patients/${memberId}`}>
            {m.name}
          </Link>
        ) : (
          <span className="rcal-m-fname">{m.name}</span>
        )}
        <span className="rcal-m-frel">{m.relation}</span>
      </td>
      <td>
        {m.recall ? (
          <span className={`rcal-m-recall ${m.recall.tone}`}>{m.recall.label}</span>
        ) : (
          <span className="rcal-m-frel">{m.fact}</span>
        )}
      </td>
      <td>
        {m.unscheduled ? (
          <>
            <span className="rcal-m-fv2">{usdWhole(m.unscheduled.amount)}</span>
            <span className="rcal-m-frel">
              {m.unscheduled.items} {m.unscheduled.items === 1 ? 'item' : 'items'}
            </span>
          </>
        ) : (
          <span className="rcal-m-frel">—</span>
        )}
      </td>
      <td>
        <span className="rcal-m-fv2 teal">{usdWhole(m.yearlyMaxLeft)}</span>
        <span className="rcal-m-bar" aria-hidden="true">
          <span className="rcal-m-bar-fill" style={{ width: `${String(pct)}%` }} />
        </span>
      </td>
    </tr>
  );
}
